/**
 * slice-4427 (ADR-0075, Accepted 24 Oct 2026, John): PowerHouse's saved window translations
 * (`crt_translate_sum`) applied to PH10 labels — the pure half (types, lookups, PB-unit width).
 * The hooks live in `useWindowTranslations.ts`.
 *
 * PB translates every DataWindow when it is built (`uo_phfc_dwdefault.sru:1354/1411` →
 * `f_phfc_translate_dw` → `uo_phfc_crt_translate_util.of_translate_dw`, sru:119-292): the rows of
 * the session language whose `datawin_name` equals the DataWindow's dataobject are replayed as one
 * `Modify()`. The backend parses those strings and keeps only what PH10 applies:
 *   - `text`    — the object's new caption;
 *   - `visible` — `false` when PB's row says `.visible=0` (hide only; a translation never reveals
 *                 a field that security or PH10 hides — ADR-0028/0067/0068);
 *   - `width`   — PB units, applied to grid columns only (John 24 Oct: web layout always wins, so
 *                 `.x/.y/.tabsequence/.font.face` and form widths are never applied).
 * Keys are lower-case on both sides (PB object names are case-insensitive in Modify).
 *
 * Every consumer passes its default (the SRD text); a missing row, an unknown object or a failed
 * fetch shows that default, so a user with no screen language sees exactly what PB shows them.
 */

export type DwObjectOverride = {
  text?: string;
  visible?: boolean;
  width?: number;
};

export type WindowTranslationOverlay = {
  /** The session's screen language (`usrs.screen_language_id` / Change Language); null = none. */
  language: string | null;
  /** PB DataWindow name (lower-case) → object name (lower-case) → override. */
  dataWindows: Record<string, Record<string, DwObjectOverride>>;
};

export const EMPTY_OVERLAY: WindowTranslationOverlay = { language: null, dataWindows: {} };

/** Defensive read of the endpoint body: anything malformed is treated as "no translations". */
export function normaliseOverlay(raw: unknown): WindowTranslationOverlay {
  if (!raw || typeof raw !== 'object') return EMPTY_OVERLAY;
  const r = raw as { language?: unknown; dataWindows?: unknown };
  const dws = r.dataWindows;
  if (!dws || typeof dws !== 'object' || Array.isArray(dws)) return EMPTY_OVERLAY;
  return {
    language: typeof r.language === 'string' ? r.language : null,
    dataWindows: dws as WindowTranslationOverlay['dataWindows'],
  };
}

/** The override for one object of one DataWindow, or undefined. */
export function dwOverride(
  overlay: WindowTranslationOverlay | undefined,
  dataObject: string | undefined,
  object: string | undefined,
): DwObjectOverride | undefined {
  if (!overlay || !dataObject || !object) return undefined;
  const dw = overlay.dataWindows[dataObject.toLowerCase()];
  if (!dw) return undefined;
  return dw[object.toLowerCase()];
}

/**
 * The translated caption, else `fallback`. Rendered as plain React text by every caller.
 *
 * The caption follows the fallback's colon style: PH10 sites that drop PB's trailing ':' from the
 * SRD text (the field component draws its own) get the translation without it too, so
 * 'Landed Cost:' lands as 'Landed Cost' where the default reads 'Standard Cost'.
 */
export function dwLabel(
  overlay: WindowTranslationOverlay | undefined,
  dataObject: string | undefined,
  object: string | undefined,
  fallback: string,
): string {
  const t = dwOverride(overlay, dataObject, object)?.text;
  if (typeof t !== 'string') return fallback;
  return matchColonStyle(t, fallback);
}

/** Drop the translation's trailing ':' when the default caption has none. */
export function matchColonStyle(text: string, fallback: string): string {
  if (fallback.trimEnd().endsWith(':')) return text;
  return text.replace(/\s*:\s*$/, '');
}

/** True when the translation hides the object (`.visible=0`). Never "un-hides". */
export function dwHidden(
  overlay: WindowTranslationOverlay | undefined,
  dataObject: string | undefined,
  object: string | undefined,
): boolean {
  return dwOverride(overlay, dataObject, object)?.visible === false;
}

/**
 * PB-unit grid-column width → px, the same scale PH10 already applies to PB column-profile widths
 * (`shared/profiles/legacyColumnProfile.ts` `applyLegacyColumnProfile`: ÷4.55, ×2 for PH10's
 * larger grid font, clamped 60–600).
 */
export function pbGridWidthPx(pbu: number): number {
  return Math.min(600, Math.max(60, Math.round(pbu / 4.55) * 2));
}

/**
 * The grid-column width a translation sets: the column object's own `.width` wins, else its
 * header text object's (PB grid DataWindows size the column by either). Undefined = unchanged.
 */
export function dwGridWidth(
  overlay: WindowTranslationOverlay | undefined,
  dataObject: string | undefined,
  column: string,
  headerObject: string,
): number | undefined {
  const w =
    dwOverride(overlay, dataObject, column)?.width ??
    dwOverride(overlay, dataObject, headerObject)?.width;
  return typeof w === 'number' && w > 0 ? pbGridWidthPx(w) : undefined;
}
